<template>
  <div class="table-view">
    <section class="settings-card">
      <div v-if="companyStore.employees.length" class="table-view__table">
        <div class="table-view__body">
          <div class="table-view__header">
            <div class="table-view__name employee">{{ $t("employee") }}</div>
            <div>{{ $t("rating") }}</div>
            <div></div>
          </div>
          <template v-for="employee in companyStore.employees">
            <div class="table-view__name">
              <VAvatar color="grey">
                <VImg
                  :src="employee.avatar"
                  :alt="employee.name"
                  v-if="
                    employee.avatar &&
                    typeof employee.avatar == 'string' &&
                    employee.avatar.length
                  "
                />
                <VIcon icon="mdi-account" v-else></VIcon>
              </VAvatar>
              <h3>{{ employee.name }}</h3>
            </div>
            <div :data-label="$t('rating')">{{ $t("no_rating_yet") }}</div>
            <div class="table-view__actions">
              <VBtn icon @click="openDialog(true, employee)">
                <v-icon small>mdi-pencil</v-icon>
              </VBtn>
              <VBtn color="red" icon @click="openDeleteDialog(employee.id!)">
                <v-icon small>mdi-delete</v-icon>
              </VBtn>
            </div>
          </template>
        </div>
      </div>

      <div v-else class="table-view__empty">
        <VIcon icon="mdi-account-group-outline" size="28" />
        <p>{{ $t("no_employees") }}</p>
      </div>

      <EmployeeDialog
        :opened="dialog"
        :employee="currentEmployee"
        @closeWithoutSave="closeDialog"
        @saveEmployee="updateEmployee"
        editable
      />
      <DeleteDialog
        v-model="deleteDialog"
        @delete="deleteEmployee"
        @cancel="employeeId = undefined"
      />
    </section>

    <VBtn class="table-view__add" @click="() => openDialog(false, newEmployee)"
      >{{ $t("add_employee") }} +</VBtn
    >

    <div v-if="newEmployee">
      <EmployeeDialog
        :opened="createDialog"
        :employee="newEmployee"
        editable
        @closeWithoutSave="closeDialog"
        @saveEmployee="saveEmployee"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import {
  createCompanyEmployee,
  deleteCompanyEmployee,
  getCompanyEmployees,
  updateCompanyEmployee,
} from "@/main/services/api.service";
import { useCompanyStore } from "@/main/stores/company.store";
import {
  CreateEmployeeDTO,
  Employee,
  UpdateEmployeeDTO,
} from "@/main/types/employee.type";
import { onMounted, ref } from "vue";
import EmployeeDialog from "../dialogs/employee-dialog.vue";
import DeleteDialog from "../delete-popup.vue";

const companyStore = useCompanyStore();

const dialog = ref(false);
const deleteDialog = ref(false);
const createDialog = ref(false);

const currentEmployee = ref<Employee>({
  email: "",
  name: "",
  username: "",
  id: undefined,
});

const newEmployee = ref<Employee>({
  email: "",
  name: "",
  username: "",
  id: undefined,
});

const employeeId = ref<number>();

onMounted(async () => {
  try {
    if (companyStore.currentCompany?.id) {
      const companyEmployees = await getCompanyEmployees(
        companyStore.currentCompany?.id
      );

      companyStore.employees = companyEmployees;
    }
  } catch (error) {
    console.error(error);
  }
});

const openDialog = (edit: boolean, employee: Employee) => {
  currentEmployee.value = employee;

  edit ? (dialog.value = true) : (createDialog.value = true);
};

const openDeleteDialog = (id: number) => {
  deleteDialog.value = true;
  employeeId.value = id;
};

const closeDialog = async () => {
  dialog.value = false;
  createDialog.value = false;
  const emptyEmployee: Employee = {
    email: "",
    name: "",
    username: "",
    id: undefined,
    phone_number: "",
    role: "employee",
    avatar: undefined,
    employee_services_attributes: [],
    schedules_attributes: [],
  };
  currentEmployee.value = emptyEmployee;
  newEmployee.value = emptyEmployee;
  return;
};

const saveEmployee = async (employeeDto: Employee) => {
  if (employeeDto.email) {
    const params: CreateEmployeeDTO = {
      ...(employeeDto as CreateEmployeeDTO),
    };
    try {
      const employee = await createCompanyEmployee(
        companyStore.currentCompany!.id!,
        params
      );

      companyStore.employees.push(employee);
    } catch (error) {
      console.error(error);
    }
  }
  closeDialog();
};

const updateEmployee = async (employeeDto: Employee) => {
  if (companyStore.currentCompany && employeeDto) {
    try {
      const employee = await updateCompanyEmployee(
        companyStore.currentCompany!.id!,
        {
          ...(employeeDto as UpdateEmployeeDTO),
        }
      );

      const index = companyStore.employees.findIndex(
        (ser) => ser.id == employee.id
      );

      if (index != -1) {
        companyStore.employees[index] = {
          ...employee,
        };

        companyStore.$patch({
          employees: companyStore.employees,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }
  closeDialog();
};

const deleteEmployee = async () => {
  if (companyStore.currentCompany && employeeId.value) {
    try {
      await deleteCompanyEmployee(
        companyStore.currentCompany.id!,
        employeeId.value
      );
      companyStore.employees = companyStore.employees.filter(
        (emp) => emp.id != employeeId.value
      );
    } catch (error) {
      console.error(error);
    }
  }
  deleteDialog.value = false;
  employeeId.value = undefined;
};
</script>

<style lang="scss" scoped>
@use "../../styles/_breakpoints" as bp;

.table-view {
  h3 {
    margin-bottom: 0;
  }

  &__header {
    grid-template-columns: repeat(3, 1fr);
  }

  &__body {
    grid-template-columns: repeat(3, 1fr);

    @include bp.tablet {
      grid-template-columns: 1fr;
    }
  }

  &__name {
    display: flex;
    align-items: center;
    column-gap: 15px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    column-gap: 15px;

    @include bp.tablet {
      justify-content: flex-start;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 8px;
    padding: 40px 16px;
    color: #696e7c;
    text-align: center;

    p {
      margin: 0;
      font-size: 14px;
    }
  }
}

.settings-card {
  background: #f7f8fa;
  border: 1px solid #eceef1;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
}
</style>
